/**
 * Application Constants
 */

/**
 * Trial Duration (in days)
 */
export const TRIAL_DURATION_DAYS = 90; // 3 months

/**
 * Pagination Limits
 */
export const DEFAULT_PAGE_LIMIT = 20;
export const MAX_PAGE_LIMIT = 100;

/**
 * Firestore Collection Names
 */
export const COLLECTIONS = {
  USERS: "users",
  BARS: "bars",
  OSM_TEST_BARS: "osmTestBars",
  BARS_BLACKLIST: "bars_blacklist",
  BAR_CLAIMS: "claimRequests", // Actual collection name in Firestore
  CLAIM_REQUESTS: "claimRequests", // Alias – same collection, used in claimsService
  BAR_ANALYTICS: "barAnalytics",
  BAR_SUGGESTIONS: "barSuggestions",
  DRINKS: "drinks",
  EVENTS: "events",
  OFFERS: "offers",
  INVITATIONS: "invitations",
} as const;

/**
 * Invitation roles
 */
export const INVITATION_ROLES = ["admin", "viewer"] as const;
export type InvitationRole = (typeof INVITATION_ROLES)[number];

/**
 * Fine-grained permission flags (stored in Firestore user doc)
 */
export const INVITATION_PERMISSIONS = [
  "analytics",
  "moderation",
  "users",
  "content",
  "subscriptions",
  "developer",
  "system",
] as const;
export type InvitationPermission = (typeof INVITATION_PERMISSIONS)[number];

export const INVITATION_PERMISSION_LABELS: Record<InvitationPermission, string> = {
  analytics: "Analytics",
  moderation: "Moderation",
  users: "Benutzerverwaltung",
  content: "Content Management",
  subscriptions: "Abonnements",
  developer: "Developer Tools",
  system: "Systemeinstellungen",
};

/** Expiry presets in days – 0 means "kein Ablauf" */
export const INVITATION_EXPIRY_PRESETS = [
  { label: "1 Tag", days: 1 },
  { label: "7 Tage", days: 7 },
  { label: "30 Tage", days: 30 },
  { label: "Kein Ablauf", days: 0 },
] as const;

/**
 * Admin Role Claim Key
 */
export const ADMIN_ROLE_CLAIM = "role";
export const ADMIN_ROLE_VALUE = "admin";

/**
 * Status Values
 */
export const CLAIM_STATUS = {
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
} as const;

export const BAR_STATUS = {
  ACTIVE: "active",
  CLOSED: "closed",
  ARCHIVED: "archived",
} as const;

export const BAR_VISIBILITY = {
  PUBLIC: "public",
  UNLISTED: "unlisted",
} as const;

/**
 * Subscription Tiers
 */
export const SUBSCRIPTION_TIERS = {
  FREE: "free",
  BASIC: "basic",
  PREMIUM: "premium",
  ENTERPRISE: "enterprise",
} as const;

/**
 * Date Formats
 */
export const DATE_FORMATS = {
  DISPLAY: "dd.MM.yyyy",
  DISPLAY_TIME: "dd.MM.yyyy HH:mm",
  ISO: "yyyy-MM-dd",
  ANALYTICS_KEY: "yyyyMMdd",
} as const;

/**
 * Error Messages
 */
export const ERROR_MESSAGES = {
  UNAUTHORIZED: "Sie haben keine Berechtigung für diese Aktion",
  NOT_ADMIN: "Diese Funktion ist nur für Administratoren verfügbar",
  NETWORK_ERROR: "Netzwerkfehler. Bitte prüfen Sie Ihre Internetverbindung",
  FIRESTORE_ERROR: "Fehler beim Laden der Daten",
  UPDATE_ERROR: "Fehler beim Aktualisieren",
  DELETE_ERROR: "Fehler beim Löschen",
  UNKNOWN_ERROR: "Ein unbekannter Fehler ist aufgetreten",
} as const;

/**
 * Success Messages
 */
export const SUCCESS_MESSAGES = {
  CLAIM_APPROVED: "Claim erfolgreich genehmigt",
  CLAIM_REJECTED: "Claim abgelehnt",
  BAR_UPDATED: "Bar erfolgreich aktualisiert",
  BAR_DELETED: "Bar gelöscht",
  DRINK_APPROVED: "Getränk genehmigt",
  DRINK_DELETED: "Getränk gelöscht",
  EVENT_UPDATED: "Event aktualisiert",
  OFFER_UPDATED: "Angebot aktualisiert",
} as const;
